import {
  createContext,
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  /** Action inline (pattern « Annuler ») — ferme le toast après clic. */
  action?: ToastAction;
  /** Durée d'affichage en ms (défaut 5000 ; rallongée quand une action est présente). */
  duration?: number;
}

interface ToastOptions {
  action?: ToastAction;
  duration?: number;
}

interface ToastContextValue {
  toast: (type: ToastType, title: string, description?: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const icons: Record<ToastType, ReactNode> = {
  success: <CheckCircle className="h-4 w-4 text-success" />,
  error: <AlertCircle className="h-4 w-4 text-danger" />,
  warning: <AlertTriangle className="h-4 w-4 text-warning" />,
  info: <Info className="h-4 w-4 text-info" />,
};

const accentColors: Record<ToastType, string> = {
  success: 'border-l-success',
  error: 'border-l-danger',
  warning: 'border-l-warning',
  info: 'border-l-info',
};

let toastCounter = 0;

/**
 * Pont impératif vers le ToastProvider, pour le code qui vit hors de l'arbre
 * React (le MutationCache global de main.tsx). `lastErrorAt` dit quand le
 * dernier toast d'erreur a été affiché : le gestionnaire global s'en sert
 * pour ne pas doubler un toast qu'un composant a déjà montré.
 */
let imperativeToast: ToastContextValue['toast'] | null = null;
let lastErrorToastAt = 0;
export const toastBridge = {
  show(type: ToastType, title: string, description?: string): void {
    imperativeToast?.(type, title, description);
  },
  get lastErrorAt(): number {
    return lastErrorToastAt;
  },
};

function ToastItem({ toast: t, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { t: tr } = useTranslation();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Une action (« Annuler ») laisse plus de temps pour réagir.
  const duration = t.duration ?? (t.action ? 8000 : 5000);

  const startTimer = () => {
    timerRef.current = setTimeout(() => {
      onDismiss(t.id);
    }, duration);
  };

  const pauseTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onDismiss(t.id);
    }, duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [t.id, duration, onDismiss]);

  return (
    <div
      role={t.type === 'error' ? 'alert' : 'status'}
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
      onFocus={pauseTimer}
      onBlur={startTimer}
      className={cn(
        'pointer-events-auto flex w-80 items-start gap-3 rounded border border-l-[3px] border-border bg-surface p-4 shadow-elevated animate-toast-in',
        accentColors[t.type],
      )}
    >
      <div className="mt-0.5 flex-shrink-0">{icons[t.type]}</div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-text">{t.title}</p>
        {t.description && (
          <p className="mt-0.5 text-xs text-subtle leading-relaxed">{t.description}</p>
        )}
        {t.action && (
          <button type="button"
            onClick={() => {
              t.action?.onClick();
              onDismiss(t.id);
            }}
            className="mt-1.5 rounded text-xs font-semibold text-accent transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {t.action.label}
          </button>
        )}
      </div>
      <button type="button"
        onClick={() => onDismiss(t.id)}
        aria-label={tr('common.closeNotification')}
        className="flex-shrink-0 rounded p-0.5 text-subtle transition-colors hover:text-text"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (type: ToastType, title: string, description?: string, options?: ToastOptions) => {
      const id = `toast-${++toastCounter}`;
      if (type === 'error') lastErrorToastAt = Date.now();
      setToasts((prev) => [
        ...prev,
        { id, type, title, description, action: options?.action, duration: options?.duration },
      ]);
    },
    [],
  );

  useEffect(() => {
    imperativeToast = toast;
    return () => {
      imperativeToast = null;
    };
  }, [toast]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
